import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import BrutalButton from "@/components/common/BrutalButton";

export default function Login() {
    const { login, user } = useAuth();
    const navigate = useNavigate();
    const [form, setForm] = useState({ email: "", password: "" });
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (user && typeof user === "object") navigate("/");
    }, [user, navigate]);

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        const res = await login(form.email, form.password);
        setLoading(false);
        if (res.ok) {
            toast.success("Signed in");
            navigate("/");
        } else {
            setError(res.error);
        }
    };

    return (
        <section data-testid="login-page" className="py-20 md:py-28">
            <div className="max-w-md mx-auto px-6">
                <div className="brutal-card bg-white p-8 md:p-10">
                    <span className="label-mono inline-block bg-brand-yellow border-2 border-ink rounded-full px-3 py-1">
                        Sign in
                    </span>
                    <h1 className="mt-5 font-display text-4xl font-black tracking-tighter">
                        Welcome back.
                    </h1>
                    <p className="mt-2 font-body text-ink/65">
                        Sign in to access the Technovale Solutions studio dashboard.
                    </p>

                    <form onSubmit={submit} className="mt-8 space-y-5" data-testid="login-form">
                        <label className="block">
                            <span className="label-mono mb-2 block">Email</span>
                            <input
                                required
                                type="email"
                                value={form.email}
                                onChange={(e) => setForm({ ...form, email: e.target.value })}
                                className="brutal-input w-full"
                                placeholder="you@company.com"
                                data-testid="login-input-email"
                            />
                        </label>
                        <label className="block">
                            <span className="label-mono mb-2 block">Password</span>
                            <input
                                required
                                type="password"
                                value={form.password}
                                onChange={(e) => setForm({ ...form, password: e.target.value })}
                                className="brutal-input w-full"
                                placeholder="••••••••"
                                data-testid="login-input-password"
                            />
                        </label>
                        {error && (
                            <div data-testid="login-error" className="bg-brand-orange text-white border-2 border-ink rounded-lg px-4 py-3 font-body text-sm">
                                {error}
                            </div>
                        )}
                        <BrutalButton
                            type="submit"
                            variant="primary"
                            size="lg"
                            className="w-full"
                            disabled={loading}
                            data-testid="login-submit-button"
                        >
                            {loading ? "Signing in…" : "Sign in"} <ArrowRight size={16} />
                        </BrutalButton>
                    </form>

                    <p className="mt-6 font-body text-sm text-ink/65">
                        Don't have an account?{" "}
                        <Link to="/register" className="font-bold underline underline-offset-4" data-testid="login-register-link">
                            Create one
                        </Link>
                    </p>
                </div>
            </div>
        </section>
    );
}
